import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { z } from "zod";

import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import {
	getRoleId,
	traxUserListSchema,
} from "~/shared/schemas/trax-user-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import {
	getAndValidate,
	postAndValidate,
} from "~/shared/services/api/fetch-client.ts";
import { useIsAuthenticated } from "~/shared/utils/use-is-authenticated.tsx";

const mutationResponseSchema = z.union([
	z.void(),
	z.null(),
	z.string(),
	z.object({}).passthrough(),
]);

export type TraxUsersQueryScope = "admin" | "vendor";

export function useTraxUsers(
	customerId: number | undefined,
	queryKeyScope: TraxUsersQueryScope,
) {
	const queryClient = useQueryClient();
	const { showError, showSuccess } = useNotificationActions();
	const { email: adminEmail } = useIsAuthenticated();

	const queryKey = useMemo(
		() => [`${queryKeyScope}Users`, customerId] as const,
		[queryKeyScope, customerId],
	);

	const {
		data: users,
		isLoading,
		isError,
		isFetching,
	} = useQuery({
		queryKey,
		queryFn: async () => {
			const route = apiRoutes.GETALLTRAXUSERSBYCUSTID.replace(
				":customerId",
				String(customerId),
			);
			const result = await getAndValidate(route, traxUserListSchema);
			if (!result.success) {
				throw new Error(result.error);
			}
			return result.data;
		},
		enabled: !!customerId,
	});

	const invalidateUsers = useCallback(() => {
		queryClient.invalidateQueries({ queryKey });
	}, [queryClient, queryKey]);

	const addMutation = useMutation({
		mutationFn: async (newUser: {
			firstName: string;
			lastName: string;
			email: string;
			role: string;
		}) => {
			const roleId = getRoleId(newUser.role);
			const body = {
				email: newUser.email,
				firstName: newUser.firstName,
				lastName: newUser.lastName,
				mobilePhoneNumber: "",
				Role: "",
				SelectedRole: {
					Role: newUser.role,
					RoleValue: roleId,
				},
				Errors: "",
				companyId: customerId,
				customerEmail: adminEmail,
				roleId,
			};
			const result = await postAndValidate(
				apiRoutes.ADDWEBUSER,
				body,
				mutationResponseSchema,
			);
			if (!result.success) {
				throw new Error(result.error);
			}
			return result.data;
		},
		onSuccess: () => {
			invalidateUsers();
			showSuccess("User added successfully.");
		},
		onError: (error: Error) => {
			showError(
				`Failed to add user. Please verify the user doesn't already exist. ${error.message}`,
			);
		},
	});

	const updateMutation = useMutation({
		mutationFn: async (updatedUser: {
			userId: string;
			firstName: string;
			lastName: string;
			email: string;
			role: string;
			mobilePhoneNumber?: string;
		}) => {
			const roleId = getRoleId(updatedUser.role);
			const body = {
				Errors: "",
				Role: "",
				Roles: {
					Role: updatedUser.role,
					RoleValue: roleId,
				},
				email: updatedUser.email,
				firstName: updatedUser.firstName,
				lastName: updatedUser.lastName,
				mobilePhoneNumber: updatedUser.mobilePhoneNumber ?? "",
				roleId,
				rowId: "",
				userId: updatedUser.userId,
			};
			const result = await postAndValidate(
				apiRoutes.UPDATEWEBUSER,
				body,
				mutationResponseSchema,
			);
			if (!result.success) {
				throw new Error(result.error);
			}
			return result.data;
		},
		onSuccess: () => {
			invalidateUsers();
			showSuccess("User updated successfully.");
		},
		onError: (error: Error) => {
			showError(error.message);
		},
	});

	const deleteMutation = useMutation({
		mutationFn: async (userId: string) => {
			const body = {
				email: "",
				firstName: "",
				lastName: "",
				mobilePhoneNumber: "",
				Role: "",
				Errors: "",
				customerId,
				rowId: "",
				userId,
			};
			const result = await postAndValidate(
				apiRoutes.DELETEWEBUSER,
				body,
				mutationResponseSchema,
			);
			if (!result.success) {
				throw new Error(result.error);
			}
			return result.data;
		},
		onSuccess: (_, userId) => {
			queryClient.setQueryData(
				queryKey,
				(oldUsers: z.infer<typeof traxUserListSchema> | undefined) => {
					if (!oldUsers) return [];
					return oldUsers.filter((user) => user.userId !== userId);
				},
			);
			invalidateUsers();
			showSuccess("User deleted successfully.");
		},
		onError: (error: Error) => {
			showError(error.message);
		},
	});

	const addUser = useCallback(
		async (newUser: {
			firstName: string;
			lastName: string;
			email: string;
			role: string;
		}) => {
			await addMutation.mutateAsync(newUser);
		},
		[addMutation],
	);

	const updateUser = useCallback(
		async (updatedUser: {
			userId: string;
			firstName: string;
			lastName: string;
			email: string;
			role: string;
			mobilePhoneNumber?: string;
		}) => {
			await updateMutation.mutateAsync(updatedUser);
		},
		[updateMutation],
	);

	const deleteUser = useCallback(
		async (userId: string) => {
			await deleteMutation.mutateAsync(userId);
		},
		[deleteMutation],
	);

	return {
		users: users ?? [],
		isLoading,
		isFetching,
		isError,
		refreshUsers: invalidateUsers,
		addUser,
		isAdding: addMutation.isPending,
		updateUser,
		isUpdating: updateMutation.isPending,
		deleteUser,
		isDeleting: deleteMutation.isPending,
	};
}
